import React from 'react';
import { Link, useLocation } from 'react-router-dom';

interface User {
  name: string;
  email: string;
  avatar?: string;
  plan: 'free' | 'pro';
}

interface MobileBottomNavProps {
  user?: User | null;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ user }) => {
  const location = useLocation();
  const [imageError, setImageError] = React.useState(false);

  // Reset image error when avatar URL changes
  React.useEffect(() => {
    setImageError(false);
  }, [user?.avatar]);

  const navItems = [
    {
      path: '/compare',
      label: 'Run',
      icon: (isActive: boolean) => (
        <svg
          className={`w-6 h-6 ${isActive ? 'text-cyan-400' : 'text-slate-400'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
      ),
    },
    {
      path: '/results',
      label: 'Results',
      icon: (isActive: boolean) => (
        <svg
          className={`w-6 h-6 ${isActive ? 'text-cyan-400' : 'text-slate-400'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 12a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1v-7z"
          />
        </svg>
      ),
    },
    {
      path: '/dashboard',
      label: 'Insights',
      icon: (isActive: boolean) => (
        <svg
          className={`w-6 h-6 ${isActive ? 'text-cyan-400' : 'text-slate-400'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      path: '/profile-menu',
      label: 'Profile',
      icon: (isActive: boolean) => {
        // Show image only if avatar exists and hasn't errored
        if (user?.avatar && !imageError) {
          return (
            <img
              src={user.avatar}
              alt={user.name}
              className={`w-12 h-12 rounded-full object-cover ${
                isActive ? 'ring-2 ring-cyan-400' : ''
              }`}
              onError={() => {
                console.warn('Failed to load profile image:', user.avatar);
                setImageError(true);
              }}
            />
          );
        }

        // Fallback to initials
        const initials = user?.name
          ? user.name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
          : 'U';

        return (
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold ${
              isActive
                ? 'bg-gradient-to-r from-cyan-400 to-blue-500 text-white ring-2 ring-cyan-400'
                : 'bg-slate-600 text-slate-300'
            }`}
          >
            {initials}
          </div>
        );
      },
    },
  ];

  const isActivePath = (path: string) => {
    if (path === '/results') {
      return location.pathname.startsWith('/results');
    }
    if (path === '/profile-menu') {
      return location.pathname === '/profile-menu' || location.pathname === '/profile' || location.pathname === '/settings' || location.pathname === '/billing' || location.pathname === '/legal';
    }
    return location.pathname === path;
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700/50 z-[9999] safe-area-inset-bottom">
      <div className="flex items-center justify-around px-2 py-3">
        {navItems.map((item) => {
          const isActive = isActivePath(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center space-y-1 touch-manipulation"
              style={{ minWidth: '60px', minHeight: '48px' }}
            >
              <div className="flex items-center justify-center">
                {item.icon(isActive)}
              </div>
              {item.path !== '/profile-menu' && (
                <span
                  className={`text-xs font-medium ${
                    isActive ? 'text-cyan-400' : 'text-slate-400'
                  }`}
                >
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
